import ReactHtmlParser from 'react-html-parser';

/**
 * Calculates the interest rate given the number of periods, payment, present value, future value, and type of interest.
 * 
 * @param {number} periods - The number of periods.
 * @param {number} payment - The payment made each period.
 * @param {number} present - The present value, i.e. the initial amount borrowed.
 * @param {number} future - The future value, i.e. the amount to be paid back.
 * @param {number} type - The type of interest, where 0 is nominal, 1 is effective.
 * @param {number} [guess] - The initial guess for the interest rate.
 * @returns {number} The interest rate.
 */
export function RATE(periods, payment, present, future, type, guess) {
    guess = (guess === undefined) ? 0.01 : guess; // Set the initial guess
    future = (future === undefined) ? 0 : future; // Set the future value
    type = (type === undefined) ? 0 : type; // Set the type of interest

    // Set maximum epsilon for end of iteration
    var epsMax = 1e-10;

    // Set maximum number of iterations
    var iterMax = 10;

    // Implement Newton's method
    var y, y0, y1, x0, x1 = 0, // Initialise variables
        f = 0,
        i = 0;
    var rate = guess;
    if (Math.abs(rate) < epsMax) {
        y = present * (1 + periods * rate) + payment * (1 + rate * type) * periods + future;
    } else {
        f = Math.exp(periods * Math.log(1 + rate));
        y = present * f + payment * (1 / rate + type) * (f - 1) + future;
    }
    y0 = present + payment * periods + future;
    y1 = present * f + payment * (1 / rate + type) * (f - 1) + future;
    i = x0 = 0;
    x1 = rate;
    while ((Math.abs(y0 - y1) > epsMax) && (i < iterMax)) {
        rate = (y1 * x0 - y0 * x1) / (y1 - y0);
        x0 = x1;
        x1 = rate;
        if (Math.abs(rate) < epsMax) {
            y = present * (1 + periods * rate) + payment * (1 + rate * type) * periods + future;
        } else {
            f = Math.exp(periods * Math.log(1 + rate));
            y = present * f + payment * (1 / rate + type) * (f - 1) + future;
        }
        y0 = y1;
        y1 = y;
        ++i;
    }
    return rate;
};

/**
 * Calculates the payment amount per period based on the interest rate, number of payments, present value, future value, and type of interest.
 *
 * @param {number} rate_per_period - The interest rate per period.
 * @param {number} number_of_payments - The total number of payments.
 * @param {number} present_value - The initial amount borrowed.
 * @param {number} future_value - The future value to be paid back.
 * @param {number} type - The type of interest (0 for nominal, 1 for effective).
 * @return {number} The payment amount per period.
 */
export function PMT(rate_per_period, number_of_payments, present_value, future_value, type){
    future_value = typeof future_value !== 'undefined' ? future_value : 0;
    type = typeof type !== 'undefined' ? type : 0;

	if(rate_per_period != 0.0){
		// Interest rate exists
		var q = Math.pow(1 + rate_per_period, number_of_payments);
		return -(rate_per_period * (future_value + (q * present_value))) / ((-1 + q) * (1 + rate_per_period * (type)));

	} else if(number_of_payments != 0.0){
		// No interest rate, but number of payments exists
		return -(future_value + present_value) / number_of_payments;
	}

	return 0;
}